/**
 * The level burst's geometry (P2.4-T41): where the art stands in device pixels for an anchor, an art pixel's size and
 * the screen, and the stepped strengths of the edge glow. Pure, so the drawing only places what these say.
 */

/**
 * How many bands the edge glow nests, outermost first.
 */
export const EDGE_BANDS = 3;

/**
 * How many art pixels thick each band of the edge glow is.
 */
export const EDGE_BAND_ART = 6;

const EDGE_STRENGTH = 0.6;
const EDGE_QUARTERS = 4;
const PILLAR_GOLD_INSET = 3;
const MIN_DOTS = 3;
const MAX_DOTS = 7;

/**
 * How many whole device pixels an art pixel is drawn at: the world camera's zoom rounded, never under 1.
 *
 * @param zoom The world camera's zoom.
 * @returns The device pixels an art pixel.
 */
export function artScaleOf(zoom: number): number {
  return Math.max(1, Math.round(zoom));
}

/**
 * The left edge of a frame of `2 x half + 1` art pixels centred on the art pixel at an anchor: that pixel's left edge
 * is `floor(k / 2)` before the anchor, so the anchor stands inside it for every k.
 *
 * @param anchor The anchor across, in device pixels.
 * @param half Art pixels either side of the centre pixel.
 * @param k Device pixels an art pixel.
 * @returns The frame's left in device pixels.
 */
export function centredLeft(anchor: number, half: number, k: number): number {
  return anchor - Math.floor(k / 2) - half * k;
}

/**
 * A box in device pixels: its left, its bottom edge, its width and its height.
 */
export interface PillarBox {
  left: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * The pillar's box: it stands on the feet's row and rises its grown share of the way to the top of the screen, so a
 * full pillar is the screen's height.
 *
 * @param anchorX The feet across, in device pixels.
 * @param anchorY The feet down.
 * @param half The pillar's half width in art pixels.
 * @param grow How much of its height has grown, 0 to 1.
 * @param k Device pixels an art pixel.
 * @returns The box.
 */
export function pillarBox(anchorX: number, anchorY: number, half: number, grow: number, k: number): PillarBox {
  const bottom = anchorY + k;
  return { left: centredLeft(anchorX, half, k), bottom, width: (2 * half + 1) * k, height: Math.round(grow * bottom) };
}

/**
 * The pillar cut at the top of the body: the solid stretch above it and the stretch over the body, which is drawn at
 * partial strength so the character shows through the light. Heights are in device pixels and tops are rows of the
 * screen; a stretch of height 0 is not drawn.
 */
export interface PillarSplit {
  upperTop: number;
  upperHeight: number;
  bodyTop: number;
  bodyHeight: number;
}

/**
 * Cuts a pillar's box where the body ends, written in place.
 *
 * @param box The pillar's box.
 * @param body How far the body reaches above the pillar's bottom edge, in device pixels.
 * @param out Where to write the split.
 * @returns `out`.
 */
export function splitPillar(box: PillarBox, body: number, out: PillarSplit): PillarSplit {
  const top = box.bottom - box.height;
  const bodyHeight = Math.min(box.height, Math.max(0, body));
  out.bodyHeight = bodyHeight;
  out.bodyTop = box.bottom - bodyHeight;
  out.upperHeight = box.height - bodyHeight;
  out.upperTop = top;
  return out;
}

/**
 * The half width of the pillar's gold, inside its dark edge.
 *
 * @param half The pillar's half width.
 * @returns The gold's half width, never under 0.
 */
export function goldHalf(half: number): number {
  return Math.max(0, half - PILLAR_GOLD_INSET);
}

/**
 * How many dots a ray of a span has: one every two art pixels, three at the least and seven at the most.
 *
 * @param span The ray's outer end less its inner end, in art pixels.
 * @returns The dots.
 */
export function rayDotsOf(span: number): number {
  return Math.min(MAX_DOTS, Math.max(MIN_DOTS, Math.round(span / 2)));
}

/**
 * The strength of one band of the edge glow at a step: the step's share of the strength, less a third a band further in,
 * rounded to a quarter so the band stays flat.
 *
 * @param step The glow's step, 0 to 4.
 * @param band The band, 0 outermost.
 * @returns The alpha, a multiple of 0.25.
 */
export function edgeAlpha(step: number, band: number): number {
  return Math.round((step / EDGE_QUARTERS) * EDGE_STRENGTH * (1 - band / EDGE_BANDS) * EDGE_QUARTERS) / EDGE_QUARTERS;
}

/**
 * One rectangle of the edge glow, in device pixels, and the band it belongs to.
 */
export interface EdgeRect {
  band: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Rectangles to write into: four a band, outermost first (top, bottom, left, right).
 *
 * @returns The rectangles.
 */
export function newEdgeRects(): EdgeRect[] {
  const rects: EdgeRect[] = [];
  for (let band = 0; band < EDGE_BANDS; band += 1) {
    for (let side = 0; side < 4; side += 1) {
      rects.push({ band, left: 0, top: 0, width: 0, height: 0 });
    }
  }
  return rects;
}

/**
 * Writes the edge glow's rectangles for a screen: each band is a ring `EDGE_BAND_ART` art pixels thick one band further
 * in than the last, cut into a top and a bottom side across the ring and a left and a right between them, so no pixel is
 * covered twice.
 *
 * @param width The screen's width in device pixels.
 * @param height Its height.
 * @param k Device pixels an art pixel.
 * @param out The rectangles from `newEdgeRects`.
 */
export function edgeRects(width: number, height: number, k: number, out: EdgeRect[]): void {
  const thick = EDGE_BAND_ART * k;
  for (let band = 0; band < EDGE_BANDS; band += 1) {
    const inset = band * thick;
    const across = Math.max(0, width - 2 * inset);
    const down = Math.max(0, height - 2 * inset - 2 * thick);
    const top = out[band * 4] as EdgeRect;
    const bottom = out[band * 4 + 1] as EdgeRect;
    const left = out[band * 4 + 2] as EdgeRect;
    const right = out[band * 4 + 3] as EdgeRect;
    top.left = inset;
    top.top = inset;
    top.width = across;
    top.height = thick;
    bottom.left = inset;
    bottom.top = height - inset - thick;
    bottom.width = across;
    bottom.height = thick;
    left.left = inset;
    left.top = inset + thick;
    left.width = thick;
    left.height = down;
    right.left = width - inset - thick;
    right.top = inset + thick;
    right.width = thick;
    right.height = down;
  }
}
